import { webLog } from '../../../shared/api/grpc/webLogger';
import { sanitizeAcceptedEndTypeConnectivityIssues } from './validationEndTypeCompatibility';
import {
  isRemovedValidationFieldName,
  isRemovedValidationId,
  isSelectableValidationId,
  normalizeValidationId,
  resolveCanonicalValidationTitle,
} from '../validationConstants';
import type {
  PcfValidationReport,
  ValidationCheckResult,
  ValidationDashboard,
  ValidationDashboardAction,
  ValidationDefinition,
  ValidationIssue,
} from '../types';

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function formatMs(ms: number): string {
  return ms.toFixed(1);
}

export function pascalize(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(pascalize);
  }
  const result: any = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    result[key] = pascalize(value);
    // Also create PascalCase variant if key starts with lowercase
    if (key.length > 0 && key[0] >= 'a' && key[0] <= 'z') {
      const pascal = key[0].toUpperCase() + key.slice(1);
      if (result[pascal] === undefined) {
        result[pascal] = result[key];
      }
    }
  }
  return result;
}

export const STATUS_MAP: Record<number, string> = { 0: 'Passed', 1: 'Failed', 2: 'Warning', 3: 'Running', 4: 'NotRun' };
export const SEVERITY_MAP: Record<number, string> = { 0: 'Info', 1: 'Warning', 2: 'Error', 3: 'Critical' };

/**
 * Normalize a parsed report: PascalCase keys at all levels + enum number→string conversion.
 */
export function normalizeReportKeys(raw: any): PcfValidationReport {
  const report = pascalize(raw);
  // Convert numeric enums to strings in CheckResults
  if (Array.isArray(report.CheckResults)) {
    for (const check of report.CheckResults) {
      if (typeof check.ValidationId === 'number') {
        check.ValidationId = normalizeValidationId(check.ValidationId);
      }
      check.Title = resolveCanonicalValidationTitle(check.ValidationId, check.Title);
      if (typeof check.Status === 'number') {
        check.Status = STATUS_MAP[check.Status] ?? 'NotRun';
      }
      if (Array.isArray(check.Issues)) {
        for (const issue of check.Issues) {
          if (typeof issue.ValidationId === 'number') {
            issue.ValidationId = normalizeValidationId(issue.ValidationId);
          }
          issue.ValidationTitle = resolveCanonicalValidationTitle(issue.ValidationId, issue.ValidationTitle);
          if (typeof issue.Severity === 'number') {
            issue.Severity = SEVERITY_MAP[issue.Severity] ?? 'Info';
          }
        }
      }
    }
  }

  if (report.Dashboard) {
    for (const stage of report.Dashboard.StageBreakdown ?? []) {
      stage.ValidationIds = (stage.ValidationIds ?? []).map((id: number) => normalizeValidationId(id));
    }

    for (const reason of report.Dashboard.TopFailureReasons ?? []) {
      reason.ValidationIds = (reason.ValidationIds ?? []).map((id: number) => normalizeValidationId(id));
      if (reason.ValidationIds.length === 1) {
        reason.Label = resolveCanonicalValidationTitle(reason.ValidationIds[0], reason.Label);
      }
    }

    for (const system of report.Dashboard.TopSystems ?? []) {
      system.ValidationIds = (system.ValidationIds ?? []).map((id: number) => normalizeValidationId(id));
    }

    for (const action of report.Dashboard.RecommendedActions ?? []) {
      action.ValidationIds = (action.ValidationIds ?? []).map((id: number) => normalizeValidationId(id));
    }

    for (const overlap of report.Dashboard.TopOverlaps ?? []) {
      overlap.LeftValidationId = normalizeValidationId(overlap.LeftValidationId);
      overlap.RightValidationId = normalizeValidationId(overlap.RightValidationId);
      overlap.LeftValidationTitle = resolveCanonicalValidationTitle(
        overlap.LeftValidationId,
        overlap.LeftValidationTitle
      );
      overlap.RightValidationTitle = resolveCanonicalValidationTitle(
        overlap.RightValidationId,
        overlap.RightValidationTitle
      );
      overlap.OverlapKey = `${overlap.LeftValidationId}:${overlap.RightValidationId}`;
    }
  }

  return sanitizeAcceptedEndTypeConnectivityIssues(removeRetiredValidationResults(report as PcfValidationReport));
}

export function parseValidationReportJson(reportJson: string, source: string): PcfValidationReport {
  const parseStartedAt = nowMs();
  const rawReport = JSON.parse(reportJson);
  if (!Array.isArray(rawReport?.CheckResults) && !Array.isArray(rawReport?.checkResults)) {
    throw new Error('Validation report is missing a CheckResults array.');
  }
  const parsedAt = nowMs();
  const parsedReport = normalizeReportKeys(rawReport);
  const normalizedAt = nowMs();

  webLog.info(
    'validation-performance',
    `${source}: reportJsonChars=${reportJson.length} parseMs=${formatMs(parsedAt - parseStartedAt)} normalizeMs=${formatMs(normalizedAt - parsedAt)} checks=${parsedReport.TotalChecks ?? 0} issues=${parsedReport.TotalIssues ?? 0}`
  );

  return parsedReport;
}

export type ValidationExportScope = 'full' | 'current';
export type ValidationExportFormat = 'json' | 'html';
export type ValidationReportEnvelope = {
  report?: PcfValidationReport;
  lastValidationDate?: string;
  lastValidationCapturedAtUtc?: string;
  lastValidationCapturedAtLocal?: string;
};

export function countIssueSeverities(issues: ValidationIssue[]): {
  critical: number;
  error: number;
  warning: number;
  info: number;
} {
  let critical = 0;
  let error = 0;
  let warning = 0;
  let info = 0;

  for (const issue of issues) {
    switch (issue.Severity) {
      case 'Critical':
        critical += 1;
        break;
      case 'Error':
        error += 1;
        break;
      case 'Warning':
        warning += 1;
        break;
      case 'Info':
        info += 1;
        break;
      default:
        break;
    }
  }

  return { critical, error, warning, info };
}

export function countCheckStatuses(status: ValidationCheckResult['Status']): {
  passed: number;
  failed: number;
  warning: number;
} {
  switch (status) {
    case 'Passed':
      return { passed: 1, failed: 0, warning: 0 };
    case 'Warning':
      return { passed: 0, failed: 0, warning: 1 };
    case 'Failed':
      return { passed: 0, failed: 1, warning: 0 };
    default:
      return { passed: 0, failed: 0, warning: 0 };
  }
}

export function resolveCheckStatusFromIssues(
  status: ValidationCheckResult['Status'],
  issues: ValidationIssue[]
): ValidationCheckResult['Status'] {
  if (issues.length === 0) {
    return status === 'NotRun' || status === 'Running' ? status : 'Passed';
  }

  return issues.some((issue) => issue.Severity === 'Critical' || issue.Severity === 'Error')
    ? 'Failed'
    : 'Warning';
}

export function normalizeValidationCheckStats(check: ValidationCheckResult): ValidationCheckResult {
  const issues = Array.isArray(check.Issues) ? check.Issues : [];
  return {
    ...check,
    Issues: issues,
    IssueCount: issues.length,
    HasIssues: issues.length > 0,
    Status: resolveCheckStatusFromIssues(check.Status, issues),
  };
}

export function buildIssuesByCategory(issues: ValidationIssue[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const issue of issues) {
    const category = issue.Category || 'General';
    counts[category] = (counts[category] || 0) + 1;
  }
  return counts;
}

export function uniqueValidationIds(validationIds: number[]): number[] {
  return Array.from(new Set(validationIds));
}

export function getSelectableValidationDefinitionIds(validations: ValidationDefinition[]): number[] {
  return uniqueValidationIds(
    (validations ?? [])
      .map((validation) => normalizeValidationId(validation.Id))
      .filter(isSelectableValidationId)
  );
}

export function filterSelectableValidationIds(
  validationIds: number[] | undefined,
  availableValidations?: ValidationDefinition[]
): number[] {
  const availableIds = availableValidations
    ? new Set(getSelectableValidationDefinitionIds(availableValidations))
    : null;

  return uniqueValidationIds(
    (validationIds ?? [])
      .map(normalizeValidationId)
      .filter(isSelectableValidationId)
      .filter((validationId) => !availableIds || availableIds.has(validationId))
  );
}

export function filterRemovedValidationIds(
  validationIds: number[] | undefined,
  validationIdsWithIssues?: ReadonlySet<number>
): number[] {
  return (validationIds ?? [])
    .map((validationId) => normalizeValidationId(validationId))
    .filter((validationId) => !isRemovedValidationId(validationId))
    .filter((validationId) => !validationIdsWithIssues || validationIdsWithIssues.has(validationId));
}

export function validationIssueReferencesRemovedField(issue: ValidationIssue): boolean {
  const additionalData = issue.AdditionalData ?? {};
  return [
    additionalData.pcfKey,
    additionalData.parameterName,
    additionalData.objectTypeParameterName,
    additionalData.skeyParameterName,
    additionalData.endType1ParameterName,
    additionalData.endType2ParameterName,
    additionalData.endType3ParameterName,
    issue.Message,
    issue.Recommendation,
  ].some(isRemovedValidationFieldName);
}

export function removeRetiredDashboardValidationData(
  dashboard: ValidationDashboard | undefined,
  validationIdsWithIssues?: ReadonlySet<number>
): ValidationDashboard | undefined {
  if (!dashboard) {
    return dashboard;
  }

  return {
    ...dashboard,
    StageBreakdown: (dashboard.StageBreakdown ?? [])
      .map((stage) => ({
        ...stage,
        ValidationIds: filterRemovedValidationIds(stage.ValidationIds, validationIdsWithIssues),
      }))
      .filter((stage) => stage.ValidationIds.length > 0),
    TopFailureReasons: (dashboard.TopFailureReasons ?? [])
      .map((reason) => ({
        ...reason,
        ValidationIds: filterRemovedValidationIds(reason.ValidationIds, validationIdsWithIssues),
      }))
      .filter(
        (reason) =>
          reason.ValidationIds.length > 0 &&
          !isRemovedValidationFieldName(reason.Label)
      ),
    TopSystems: (dashboard.TopSystems ?? [])
      .map((system) => ({
        ...system,
        ValidationIds: filterRemovedValidationIds(system.ValidationIds, validationIdsWithIssues),
      }))
      .filter((system) => system.ValidationIds.length > 0),
    RecommendedActions: (dashboard.RecommendedActions ?? [])
      .map((action) => ({
        ...action,
        ValidationIds: filterRemovedValidationIds(action.ValidationIds, validationIdsWithIssues),
      }))
      .filter(
        (action) =>
          action.ValidationIds.length > 0 &&
          ![action.Title, action.Why, action.SuggestedAction].some(isRemovedValidationFieldName)
      ),
    TopOverlaps: (dashboard.TopOverlaps ?? [])
      .map((overlap) => ({
        ...overlap,
        LeftValidationId: normalizeValidationId(overlap.LeftValidationId),
        RightValidationId: normalizeValidationId(overlap.RightValidationId),
      }))
      .filter(
        (overlap) =>
          !isRemovedValidationId(overlap.LeftValidationId) &&
          !isRemovedValidationId(overlap.RightValidationId) &&
          (!validationIdsWithIssues ||
            (validationIdsWithIssues.has(overlap.LeftValidationId) ||
              validationIdsWithIssues.has(overlap.RightValidationId)))
      )
      .map((overlap) => ({
        ...overlap,
        OverlapKey: `${overlap.LeftValidationId}:${overlap.RightValidationId}`,
      })),
  };
}

export function removeRetiredValidationResults(report: PcfValidationReport): PcfValidationReport {
  const checkResults = (report.CheckResults ?? [])
    .filter((check) => !isRemovedValidationId(check.ValidationId))
    .map((check) =>
      normalizeValidationCheckStats({
        ...check,
        Issues: (check.Issues ?? []).filter(
          (issue) =>
            !isRemovedValidationId(issue.ValidationId) &&
            !validationIssueReferencesRemovedField(issue)
        ),
      })
    );

  const allIssues = checkResults.flatMap((check) => check.Issues || []);
  const validationIdsWithIssues = new Set(
    allIssues.map((issue) => normalizeValidationId(issue.ValidationId))
  );
  const severityTotals = countIssueSeverities(allIssues);
  const statusTotals = checkResults.reduce(
    (totals, check) => {
      const checkTotals = countCheckStatuses(check.Status);
      totals.passed += checkTotals.passed;
      totals.failed += checkTotals.failed;
      totals.warning += checkTotals.warning;
      return totals;
    },
    { passed: 0, failed: 0, warning: 0 }
  );

  return {
    ...report,
    CheckResults: checkResults,
    TotalChecks: checkResults.length,
    PassedChecks: statusTotals.passed,
    FailedChecks: statusTotals.failed,
    WarningChecks: statusTotals.warning,
    TotalIssues: allIssues.length,
    CriticalIssues: severityTotals.critical,
    ErrorIssues: severityTotals.error,
    WarningIssues: severityTotals.warning,
    InfoIssues: severityTotals.info,
    IsExportReady: statusTotals.failed === 0 && severityTotals.critical === 0 && severityTotals.error === 0,
    IssuesByCategory: buildIssuesByCategory(allIssues),
    Dashboard: removeRetiredDashboardValidationData(report.Dashboard, validationIdsWithIssues),
  };
}

export function mergeValidationReportCheckResults(
  currentReport: PcfValidationReport | null,
  focusedReport: PcfValidationReport
): PcfValidationReport {
  if (!currentReport || !Array.isArray(currentReport.CheckResults) || currentReport.CheckResults.length === 0) {
    return focusedReport;
  }

  const replacementChecks = new Map<number, ValidationCheckResult>();
  for (const check of focusedReport.CheckResults || []) {
    replacementChecks.set(normalizeValidationId(check.ValidationId), normalizeValidationCheckStats(check));
  }

  if (replacementChecks.size === 0) {
    return currentReport;
  }

  const consumedReplacementIds = new Set<number>();
  const mergedCheckResults = currentReport.CheckResults.map((check) => {
    const validationId = normalizeValidationId(check.ValidationId);
    const replacement = replacementChecks.get(validationId);
    if (!replacement) {
      return normalizeValidationCheckStats(check);
    }

    consumedReplacementIds.add(validationId);
    return replacement;
  });

  for (const [validationId, replacement] of replacementChecks) {
    if (!consumedReplacementIds.has(validationId)) {
      mergedCheckResults.push(replacement);
    }
  }

  const allIssues = mergedCheckResults.flatMap((check) => check.Issues || []);
  const severityTotals = countIssueSeverities(allIssues);
  const statusTotals = mergedCheckResults.reduce(
    (totals, check) => {
      const checkTotals = countCheckStatuses(check.Status);
      totals.passed += checkTotals.passed;
      totals.failed += checkTotals.failed;
      totals.warning += checkTotals.warning;
      return totals;
    },
    { passed: 0, failed: 0, warning: 0 }
  );

  return {
    ...currentReport,
    DocumentName: focusedReport.DocumentName || currentReport.DocumentName,
    DocumentPath: focusedReport.DocumentPath || currentReport.DocumentPath,
    ValidationDate: focusedReport.ValidationDate || currentReport.ValidationDate,
    LastValidationDate: focusedReport.LastValidationDate || currentReport.LastValidationDate,
    LastValidationCapturedAtUtc: focusedReport.LastValidationCapturedAtUtc || currentReport.LastValidationCapturedAtUtc,
    LastValidationCapturedAtLocal: focusedReport.LastValidationCapturedAtLocal || currentReport.LastValidationCapturedAtLocal,
    CheckResults: mergedCheckResults,
    TotalChecks: mergedCheckResults.length,
    PassedChecks: statusTotals.passed,
    FailedChecks: statusTotals.failed,
    WarningChecks: statusTotals.warning,
    TotalIssues: allIssues.length,
    CriticalIssues: severityTotals.critical,
    ErrorIssues: severityTotals.error,
    WarningIssues: severityTotals.warning,
    InfoIssues: severityTotals.info,
    IsExportReady: statusTotals.failed === 0 && severityTotals.critical === 0 && severityTotals.error === 0,
    IssuesByCategory: buildIssuesByCategory(allIssues),
  };
}

function resolveImpactLabel(impactScore: number, blockingIssueCount: number, warningIssueCount: number): string {
  if (blockingIssueCount >= 10 || impactScore >= 60) {
    return 'Critical';
  }

  if (blockingIssueCount > 0 || impactScore >= 25) {
    return 'High';
  }

  if (warningIssueCount > 0 || impactScore >= 10) {
    return 'Medium';
  }

  return 'Low';
}

function parseIssueSystemNames(issue: ValidationIssue): string[] {
  const raw = (issue.AdditionalData?.systemName || '').trim();
  if (!raw) {
    return ['(Unassigned System)'];
  }

  const prefix = 'Multiple systems (';
  if (!raw.startsWith(prefix) || !raw.endsWith(')')) {
    return [raw];
  }

  const inner = raw.slice(prefix.length, -1);
  const parsed = inner
    .split(',')
    .map((value) => value.replace('...', '').trim())
    .filter((value) => value.length > 0);

  return parsed.length > 0 ? parsed : [raw];
}

function aggregateTopSystems(issues: ValidationIssue[]) {
  const bySystem = new Map<string, {
    SystemName: string;
    IssueCount: number;
    BlockingIssueCount: number;
    WarningIssueCount: number;
    InfoIssueCount: number;
    ValidationIds: number[];
  }>();

  for (const issue of issues) {
    for (const systemName of parseIssueSystemNames(issue)) {
      const current = bySystem.get(systemName) || {
        SystemName: systemName,
        IssueCount: 0,
        BlockingIssueCount: 0,
        WarningIssueCount: 0,
        InfoIssueCount: 0,
        ValidationIds: [],
      };

      current.IssueCount += 1;
      if (issue.Severity === 'Critical' || issue.Severity === 'Error') {
        current.BlockingIssueCount += 1;
      } else if (issue.Severity === 'Warning') {
        current.WarningIssueCount += 1;
      } else {
        current.InfoIssueCount += 1;
      }

      if (!current.ValidationIds.includes(issue.ValidationId)) {
        current.ValidationIds.push(issue.ValidationId);
      }

      bySystem.set(systemName, current);
    }
  }

  return Array.from(bySystem.values())
    .sort((left, right) => {
      if (right.BlockingIssueCount !== left.BlockingIssueCount) {
        return right.BlockingIssueCount - left.BlockingIssueCount;
      }
      return right.IssueCount - left.IssueCount;
    })
    .slice(0, 10);
}

export function buildScopedDashboard(
  report: PcfValidationReport,
  selectedCheck: ValidationCheckResult,
  selectedIssues: ValidationIssue[],
  severityTotals: { critical: number; error: number; warning: number; info: number }
): ValidationDashboard {
  const blockingIssueCount = severityTotals.critical + severityTotals.error;
  const warningIssueCount = severityTotals.warning;
  const infoIssueCount = severityTotals.info;
  const issueCount = selectedIssues.length;
  const impactScore = (blockingIssueCount * 8) + (warningIssueCount * 3) + infoIssueCount;
  const stageFromReport = (report.Dashboard?.StageBreakdown || [])
    .find((stage) => (stage.ValidationIds || []).includes(selectedCheck.ValidationId));
  const stageKey = stageFromReport?.StageKey || 'current_check';
  const stageLabel = stageFromReport?.StageLabel || `Validation #${selectedCheck.ValidationId}`;
  const topSystems = aggregateTopSystems(selectedIssues);

  const fallbackActionTitle = selectedIssues[0]?.Recommendation
    || 'Review issues in this validation and resolve mapped problems';
  const recommendationCandidates = Array.from(
    new Set(selectedIssues.map((issue) => (issue.Recommendation || '').trim()).filter(Boolean))
  );
  const actionSource: ValidationDashboardAction[] =
    (report.Dashboard?.RecommendedActions || [])
      .filter((action) => (action.ValidationIds || []).includes(selectedCheck.ValidationId));
  const recommendedActions: ValidationDashboardAction[] = [];

  if (actionSource.length > 0) {
    for (const action of actionSource) {
      recommendedActions.push({
        ...action,
        IssueCount: issueCount,
        BlockingIssueCount: blockingIssueCount,
        WarningIssueCount: warningIssueCount,
        InfoIssueCount: infoIssueCount,
        ImpactScore: impactScore,
        ImpactLabel: resolveImpactLabel(impactScore, blockingIssueCount, warningIssueCount),
        ValidationIds: [selectedCheck.ValidationId],
      });
    }
  } else {
    const recs = recommendationCandidates.length > 0 ? recommendationCandidates : [fallbackActionTitle];
    recs.slice(0, 3).forEach((recommendation, index) => {
      recommendedActions.push({
        ActionKey: `check_${selectedCheck.ValidationId}_action_${index + 1}`,
        Priority: index + 1,
        StageKey: stageKey,
        StageLabel: stageLabel,
        Title: selectedCheck.Title,
        Why: selectedCheck.Description,
        SuggestedAction: recommendation,
        IssueCount: issueCount,
        BlockingIssueCount: blockingIssueCount,
        WarningIssueCount: warningIssueCount,
        InfoIssueCount: infoIssueCount,
        ImpactScore: impactScore,
        ImpactLabel: resolveImpactLabel(impactScore, blockingIssueCount, warningIssueCount),
        ValidationIds: [selectedCheck.ValidationId],
      });
    });
  }

  return {
    ReadinessState:
      blockingIssueCount > 0 ? 'Blocked' :
      warningIssueCount > 0 ? 'ReadyWithWarnings' : 'Ready',
    BlockingIssueCount: blockingIssueCount,
    WarningIssueCount: warningIssueCount,
    InfoIssueCount: infoIssueCount,
    AffectedSystemsCount: topSystems.length,
    FailingChecksCount: selectedCheck.Status === 'Passed' && issueCount === 0 ? 0 : 1,
    StageBreakdown: [
      {
        StageKey: stageKey,
        StageLabel: stageLabel,
        IssueCount: issueCount,
        BlockingIssueCount: blockingIssueCount,
        WarningIssueCount: warningIssueCount,
        InfoIssueCount: infoIssueCount,
        FailingCheckCount: selectedCheck.Status === 'Passed' && issueCount === 0 ? 0 : 1,
        AffectedSystemsCount: topSystems.length,
        ImpactScore: impactScore,
        ImpactLabel: resolveImpactLabel(impactScore, blockingIssueCount, warningIssueCount),
        ValidationIds: [selectedCheck.ValidationId],
      },
    ],
    TopFailureReasons: issueCount > 0 ? [
      {
        ReasonKey: `check_${selectedCheck.ValidationId}`,
        Label: selectedCheck.Title || `Validation #${selectedCheck.ValidationId}`,
        StageKey: stageKey,
        StageLabel: stageLabel,
        IssueCount: issueCount,
        BlockingIssueCount: blockingIssueCount,
        AffectedSystemsCount: topSystems.length,
        ValidationIds: [selectedCheck.ValidationId],
      },
    ] : [],
    TopSystems: topSystems,
    TopOverlaps: [],
    RecommendedActions: recommendedActions,
  };
}

export function buildScopedReport(
  report: PcfValidationReport,
  scope: ValidationExportScope,
  validationId?: number,
  selectedCheckOverride?: ValidationCheckResult | null
): { report: PcfValidationReport; check: ValidationCheckResult | null } | null {
  if (scope === 'full') {
    return { report, check: null };
  }

  const selectedCheck = selectedCheckOverride
    ?? report.CheckResults.find((check) => check.ValidationId === validationId);
  if (!selectedCheck) {
    return null;
  }

  const selectedIssues = selectedCheck.Issues || [];
  const severityTotals = countIssueSeverities(selectedIssues);
  const statusTotals = countCheckStatuses(selectedCheck.Status);

  return {
    check: selectedCheck,
    report: {
      ...report,
      TotalChecks: 1,
      PassedChecks: statusTotals.passed,
      FailedChecks: statusTotals.failed,
      WarningChecks: statusTotals.warning,
      TotalIssues: selectedIssues.length,
      CriticalIssues: severityTotals.critical,
      ErrorIssues: severityTotals.error,
      WarningIssues: severityTotals.warning,
      InfoIssues: severityTotals.info,
      IsExportReady: selectedCheck.Status === 'Passed' && selectedIssues.length === 0,
      CheckResults: [selectedCheck],
      TotalExecutionTime: selectedCheck.ExecutionTime,
      IssuesByCategory: buildIssuesByCategory(selectedIssues),
      Dashboard: buildScopedDashboard(report, selectedCheck, selectedIssues, severityTotals),
    },
  };
}

export function createHtmlFromReport(report: PcfValidationReport): string {
  const escaped = JSON.stringify(report, null, 2)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return `<!doctype html><html><head><meta charset="utf-8"><title>PCF validation report</title></head><body><pre>${escaped}</pre></body></html>`;
}
